import type { AdjustmentKey, Adjustments } from './PriceSimulation.types';
import { forEachPricePoint, isTiered, isUnsavedModification, PRICING_SCHEME, resolvePlanChange, toPlanKey, toProductKey } from './priceSimulationModel';
import type { PackageSimulationDto, PlanChange, PricePointRef, PricingSchemeType, TieredPricePointDto } from './priceSimulationModel';

export interface ProposalModification {
    TierId?: string;
    ProductGroupId: string | undefined;
    ProductId?: string;
    PlanIdentifier: string;
    PricingScheme: PricingSchemeType;
    AdjustmentPercentage: number;
    NewPrice: number | null;
    BasePriceAtCreation: number;
    TieredPrices: TieredPricePointDto[];
    IsKept: boolean;
    Status: number;
}

export interface PackageProposalPayload {
    packageId: string;
    versionId?: string;
    modifications: ProposalModification[];
}

interface ModificationPrices {
    adjustmentPercentage: number;
    newPrice: number | null;
    tieredPrices: TieredPricePointDto[];
    isKept: boolean;
}

const toModification = (
    { tier, group, product, planIdentifier, pricePoint }: PricePointRef,
    { adjustmentPercentage, newPrice, tieredPrices, isKept }: ModificationPrices
): ProposalModification => ({
    TierId: tier.tierId,
    ProductGroupId: group.groupId,
    ProductId: product.productId,
    PlanIdentifier: planIdentifier,
    PricingScheme: pricePoint.pricingScheme ?? PRICING_SCHEME.Flat,
    AdjustmentPercentage: adjustmentPercentage,
    NewPrice: newPrice,
    BasePriceAtCreation: pricePoint.currentPrice ?? 0,
    TieredPrices: tieredPrices,
    IsKept: isKept,
    Status: 0,
});

const toUnsavedModification = (ref: PricePointRef, change: PlanChange): ProposalModification =>
    isTiered(ref.pricePoint)
        ? toModification(ref, { adjustmentPercentage: 0, newPrice: null, tieredPrices: change.tieredPrices, isKept: false })
        : toModification(ref, { adjustmentPercentage: change.adjustmentPercentage, newPrice: change.newPrice, tieredPrices: [], isKept: change.isKept });

export const toSavedModification = (ref: PricePointRef): ProposalModification => {
    const { pricePoint } = ref;
    const isKept = pricePoint.isKept === true;
    const currentPrice = pricePoint.currentPrice ?? 0;
    const tieredPrices = (pricePoint.tieredPrices ?? []).map((tp) => (tp.isKept === true ? { ...tp, adjustmentPercentage: 0, newUnitPrice: tp.currentUnitPrice } : tp));
    return isTiered(pricePoint)
        ? toModification(ref, { adjustmentPercentage: pricePoint.adjustmentPercentage ?? 0, newPrice: null, tieredPrices, isKept: false })
        : toModification(ref, {
              adjustmentPercentage: isKept ? 0 : (pricePoint.adjustmentPercentage ?? 0),
              newPrice: isKept ? currentPrice : (pricePoint.newPrice ?? currentPrice),
              tieredPrices: [],
              isKept,
          });
};

export function extractDraftModifications(
    packageDetailsMap: Record<string, PackageSimulationDto | undefined>,
    selectedPackageIds: string[],
    adjustments: Adjustments
): PackageProposalPayload[] {
    const results: PackageProposalPayload[] = [];

    for (const packageId of selectedPackageIds) {
        const pkg = packageDetailsMap[packageId];
        if (!pkg?.tiers) continue;

        const modifications: ProposalModification[] = [];
        forEachPricePoint(pkg, packageId, (ref) => {
            const change = resolvePlanChange(ref.pricePoint, adjustments[ref.productKey]?.[ref.planKey]);
            if (isUnsavedModification(ref.pricePoint, change)) modifications.push(toUnsavedModification(ref, change));
        });

        if (modifications.length > 0) {
            results.push({ packageId, versionId: pkg.versionId, modifications });
        }
    }

    return results;
}

export const toAdjustmentKeys = (packages: PackageProposalPayload[]): AdjustmentKey[] =>
    packages.flatMap(({ packageId, modifications }) =>
        modifications.map((m) => ({
            productId: toProductKey(packageId, m.TierId, m.ProductGroupId, m.ProductId),
            planId: toPlanKey(packageId, m.TierId, m.PlanIdentifier, m.ProductId),
        }))
    );

export default extractDraftModifications;
